// Non-ABD shipment popup with two TABS, each an independent flow with its own button:
//   Tab 1 "PO & Receiver Details" -> Save Details -> /additional_documents (PO required)
//   Tab 2 "Documents" (DocumentsModal) -> Upload  -> /additional_documents/upload (no PO)
// Both tab panels stay mounted (display toggled) so unsaved input/file picks survive tab switches.
import { useEffect, useState } from "react";
import { Grid, Tabs, Tab } from "@mui/material";
import { Box } from "@mui/system";
import { LoadingButton } from "@mui/lab";
import { useForm } from "react-hook-form";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { apiConstants, postMultipart } from "../../../common/constants";
import { formatDate } from "../../../helpers/UtilityHelper";
import { FormProvider, RHFDateField, RHFTextField } from "../../../common/hook-form";
import DocumentsModal from "../DocumentsModal";

function AddAdditionalDocuments({ rowData, setOpen, setReload }) {
    const { enqueueSnackbar } = useSnackbar();
    const [savingDetails, setSavingDetails] = useState(false);
    const [tab, setTab] = useState(0);

    const phoneRegex = /^[0-9]{10}$/;
    const Schema = Yup.object().shape({
        poNumber: Yup.string().required("PO Number is required"),
        poExpiry: Yup.date().required("PO Expiry Date is required"),
        altContact1: Yup.string().nullable().notRequired().test("phone1", "Must be 10 digits", v => !v || phoneRegex.test(v)),
        altContact2: Yup.string().nullable().notRequired().test("phone2", "Must be 10 digits", v => !v || phoneRegex.test(v)),
    });
    const methods = useForm({
        resolver: yupResolver(Schema),
        defaultValues: { poNumber: "", poExpiry: null, altContact1: "", altContact2: "" },
    });
    const { handleSubmit, setValue } = methods;

    useEffect(() => {
        const ad = rowData?.additionalDocuments;
        if (!ad) return;
        if (ad.poNumber) setValue("poNumber", ad.poNumber);
        if (ad.poExpiry) setValue("poExpiry", dayjs(ad.poExpiry).toDate());
        if (ad.altContact1) setValue("altContact1", ad.altContact1);
        if (ad.altContact2) setValue("altContact2", ad.altContact2);
    }, [rowData, setValue]);

    const onSaveDetails = async (data) => {
        setSavingDetails(true);
        try {
            const payload = {
                awbNumber: rowData?.awb_number,
                poNumber: data.poNumber,
                poExpiry: formatDate(data.poExpiry, "yyyy-MM-dd"),
                altContact1: data.altContact1 || null,
                altContact2: data.altContact2 || null,
            };
            const fd = new FormData();
            fd.append("data", new Blob([JSON.stringify(payload)], { type: "application/json" }));
            await postMultipart(apiConstants.ADDITIONAL_DOCUMENTS, fd);
            enqueueSnackbar("Details saved", { variant: "success" });
            setReload?.(prev => prev + 1);
        } catch (err) {
            enqueueSnackbar(err?.response?.data?.message || "Something went wrong", { variant: "error" });
        }
        setSavingDetails(false);
    };

    return (
        <Box>
            <Tabs
                value={tab}
                onChange={(e, v) => setTab(v)}
                sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
            >
                <Tab label="PO & Receiver Details" />
                <Tab label="Documents" />
            </Tabs>

            <Box sx={{ display: tab === 0 ? "block" : "none" }}>
                <FormProvider methods={methods} onSubmit={handleSubmit(onSaveDetails)}>
                    <Grid container spacing={2}>
                        <Grid item xs={6}><RHFTextField name="poNumber" label="PO Number" color="secondary" variant="outlined" /></Grid>
                        <Grid item xs={6}><RHFDateField disablePast name="poExpiry" label="PO Expiry Date" /></Grid>
                        <Grid item xs={6}><RHFTextField name="altContact1" label="Alternate Receiver Contact 1" color="secondary" variant="outlined" /></Grid>
                        <Grid item xs={6}><RHFTextField name="altContact2" label="Alternate Receiver Contact 2" color="secondary" variant="outlined" /></Grid>
                    </Grid>
                    <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1.5 }}>
                        <LoadingButton loading={savingDetails} variant="contained" size="small" type="submit">Save Details</LoadingButton>
                    </Box>
                </FormProvider>
            </Box>

            <Box sx={{ display: tab === 1 ? "block" : "none" }}>
                <DocumentsModal rowData={rowData} setOpen={setOpen} setReload={setReload} />
            </Box>
        </Box>
    );
}

export default AddAdditionalDocuments;
